import { endpoints } from '@gopinhos/shared';
import { Download, FileIcon, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLeadDocuments, useUploadLeadDocument } from '../../hooks/use-lead-documents';
import { apiClient } from '../../lib/api-client';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';

export function Documents({ leadId }: { leadId: string }): JSX.Element {
  const docs = useLeadDocuments(leadId);
  const upload = useUploadLeadDocument(leadId);
  const fileRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const [progressLabel, setProgressLabel] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setProgressLabel(t('documents.hashing', { name: file.name }));
            try {
              await upload.mutateAsync(file);
              setProgressLabel(null);
            } catch (err) {
              setProgressLabel(err instanceof Error ? err.message : t('documents.uploadFailed'));
            } finally {
              if (fileRef.current) fileRef.current.value = '';
            }
          }}
        />
        <Button onClick={() => fileRef.current?.click()} disabled={upload.isPending}>
          <Upload className="h-4 w-4" />
          {upload.isPending ? t('documents.uploading') : t('documents.upload')}
        </Button>
        {progressLabel && <span className="text-xs text-surface-500">{progressLabel}</span>}
      </div>

      {docs.isLoading ? (
        <Skeleton className="h-20" />
      ) : (
        <ul className="space-y-2">
          {(docs.data?.items ?? []).map((doc) => (
            <li
              key={doc.id}
              className="flex items-center justify-between rounded-md border border-surface-200 px-3 py-2"
            >
              <div className="flex items-center gap-3">
                <FileIcon className="h-5 w-5 text-surface-400" />
                <div>
                  <p className="text-sm font-medium">{doc.originalFilename}</p>
                  <p className="text-xs text-surface-500">
                    {(doc.sizeBytes / 1024).toFixed(1)} KB · {doc.virusScanStatus}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  const resp = (await apiClient.request({
                    method: 'GET',
                    path: endpoints.documentDownload(doc.id),
                  })) as { url: string };
                  window.open(resp.url, '_blank', 'noopener');
                }}
              >
                <Download className="h-4 w-4" /> {t('documents.download')}
              </Button>
            </li>
          ))}
          {docs.data && docs.data.items.length === 0 && (
            <li className="text-sm text-surface-500">{t('documents.empty')}</li>
          )}
        </ul>
      )}
    </div>
  );
}
